import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExamPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [examData, setExamData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchExamQuestions();
  }, [courseId, isAuthenticated]);

  const fetchExamQuestions = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/exams/course/${courseId}/questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExamData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar prova');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const handleSubmit = async () => {
    // Verificar se todas as questões foram respondidas
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < examData.total_questions) {
      if (!window.confirm(`Você respondeu ${answeredCount} de ${examData.total_questions} questões. Deseja enviar mesmo assim?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      const submission = {
        course_id: parseInt(courseId),
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          question_id: parseInt(questionId),
          answer: answer
        }))
      };

      const response = await axios.post(
        `${API_URL}/api/exams/course/${courseId}/submit`,
        submission,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar prova');
    } finally {
      setSubmitting(false);
    }
  };

  const getOptionLabel = (index) => {
    return ['A', 'B', 'C', 'D'][index];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Carregando prova...</p>
        </div>
      </div>
    );
  }

  if (error && !examData) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Erro</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate(-1)} className="bg-red-600 hover:bg-red-700">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  // Mostrar resultado
  if (result) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header do Resultado */}
          <Card className={`mb-8 ${result.passed ? 'bg-green-900/30 border-green-700' : 'bg-red-900/30 border-red-700'}`}>
            <CardContent className="pt-8 pb-8 text-center">
              {result.passed ? (
                <>
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                  <h1 className="text-3xl font-bold text-green-400 mb-2">APROVADO!</h1>
                  <p className="text-white text-lg mb-4">
                    Parabéns! Você concluiu o curso com sucesso.
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                  <h1 className="text-3xl font-bold text-red-400 mb-2">REPROVADO</h1>
                  <p className="text-white text-lg mb-4">
                    Você não atingiu a nota mínima. Revise o conteúdo e tente novamente.
                  </p>
                </>
              )}
              
              <div className="flex justify-center gap-8 mt-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">{result.score.toFixed(1)}</div>
                  <div className="text-sm text-gray-400">Nota Final</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">{result.correct_count}/{result.total_questions}</div>
                  <div className="text-sm text-gray-400">Acertos</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">8.0</div>
                  <div className="text-sm text-gray-400">Nota Mínima</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questões Erradas (apenas se reprovado) */}
          {!result.passed && (
            <Card className="bg-zinc-900 border-zinc-800 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="text-red-500" />
                  Questões Incorretas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.results.filter(r => !r.is_correct).map((res) => (
                  <div key={res.question_id} className="p-4 bg-red-900/20 rounded-lg border border-red-800">
                    <p className="text-white font-medium mb-2">
                      Questão {res.question_id}: {res.question}
                    </p>
                    <div className="flex gap-4 text-sm">
                      <span className="text-red-400">
                        Sua resposta: {res.user_answer.toUpperCase()}
                      </span>
                      <span className="text-green-400">
                        Resposta correta: {res.correct_answer.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Botões de Ação */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {result.passed && result.certificate_id && (
              <>
                <Button
                  onClick={() => navigate(`/certificate/${result.certificate_id}`)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="view-certificate-btn"
                >
                  <CheckCircle className="mr-2" size={18} />
                  Ver Certificado
                </Button>
              </>
            )}
            
            {!result.passed && (
              <Button
                onClick={() => {
                  setResult(null);
                  setAnswers({});
                  setCurrentQuestion(0);
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Tentar Novamente
              </Button>
            )}
            
            <Button
              onClick={() => navigate(`/course/${courseId}`)}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <ArrowLeft className="mr-2" size={18} />
              Voltar ao Curso
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Prova em andamento
  const question = examData.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate(`/course/${courseId}`)}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar ao Curso
          </Button>
          
          <h1 className="text-2xl font-bold text-white mb-2">
            Avaliação: {examData.course_title}
          </h1>
          
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <HelpCircle size={16} />
              {examData.total_questions} questões
            </span>
            <span className="flex items-center gap-1">
              <Clock size={16} />
              Nota mínima: 8.0
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progresso</span>
            <span>{answeredCount}/{examData.total_questions} respondidas</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-600 transition-all duration-300"
              style={{ width: `${(answeredCount / examData.total_questions) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {examData.questions.map((q, idx) => (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-10 h-10 rounded-lg font-medium transition-all ${
                currentQuestion === idx
                  ? 'bg-red-600 text-white'
                  : answers[q.id]
                    ? 'bg-green-600/30 text-green-400 border border-green-600'
                    : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {/* Current Question */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white">
              Questão {currentQuestion + 1} de {examData.total_questions}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg text-white mb-6">{question.question}</p>
            
            <div className="space-y-3">
              {question.options.map((option, idx) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswerSelect(question.id, option.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    answers[question.id] === option.id
                      ? 'bg-red-600/20 border-red-600 text-white'
                      : 'bg-zinc-800 border-zinc-700 text-gray-300 hover:border-zinc-600'
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                    answers[question.id] === option.id
                      ? 'bg-red-600 text-white'
                      : 'bg-zinc-700 text-gray-400'
                  }`}>
                    {getOptionLabel(idx)}
                  </span>
                  {option.text}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            Anterior
          </Button>
          
          {currentQuestion < examData.total_questions - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              className="bg-red-600 hover:bg-red-700"
            >
              Próxima
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-exam-btn"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2" size={18} />
                  Finalizar Prova
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamPage;
